import { useState, useEffect } from "react";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import Avatar from "@mui/material/Avatar";
import { Box, Card, CircularProgress, FormControl, FormGroup, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography, Switch, Divider } from "@mui/material";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import styled from "@emotion/styled";
import Span from "@jumbo/shared/Span";
import { Edit } from "@mui/icons-material";
import SyncIcon from "@mui/icons-material/Sync";
import Radio from "@mui/material/Radio";
import Div from "@jumbo/shared/Div";
import RadioGroup from "@mui/material/RadioGroup";
import { toast } from "react-toastify";
import { axios_tenant } from "../../../axios";
import Checkbox from "@mui/material/Checkbox";
import {
    sortIntentsByCategory,
    getAllIpAppIds,
    addIpAppIds,
    removeIpAppIds,
    toggleIpAppIds,
    getAppIntentSelectionState
} from "app/utils/intentUtils";
import { getTenantName } from "app/utils/tenantUtils";

const oaWorkflowTenants = (process.env.REACT_APP_INTERNAL_TENANTS || "trexo-ops")
    .split(",").map(t => t.trim().toLowerCase());
const isOaWorkflowTenant = oaWorkflowTenants.includes(getTenantName()?.toLowerCase());


const Item = styled(Span)(({ theme }) => ({
    padding: theme.spacing(0, 1)
}));

const UserCard = ({ user, getUserData, allApps = [] }) => {
    const initialState = {
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.mail,
        role: user.role,
        status: user.status,
        apps: user.apps || []
    };

    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedValue, setSelectedValue] = useState(user.status);
    const [selectedApps, setSelectedApps] = useState(user.apps || []);
    const [tempSelectedApps, setTempSelectedApps] = useState([]);
    const [inputData, setInputData] = useState(initialState);
    const [loading, setLoading] = useState(false);

    const [appToggle, setAppToggle] = useState({});
    const [tempSelectedIpApps, setTempSelectedIpApps] = useState([]);
    const [oaShellAccess, setOaShellAccess] = useState(!!user.oaRole);
    const [oaRole, setOaRole] = useState(user.oaRole || "");
    const [pgsAccess, setPgsAccess] = useState(!!user.pgsRole);
    const [pgsRole, setPgsRole] = useState(user.pgsRole || "");
    
    // KOT State
    const [availableKotRoles, setAvailableKotRoles] = useState([]);
    const [tempKotRole, setTempKotRole] = useState("");
    const [fetchingKotData, setFetchingKotData] = useState(false);

    const { firstName, lastName, email, role, status } = inputData;

    const style = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "60%",
        maxHeight: "90vh",
        overflowY: "auto",
        bgcolor: "background.paper",
        border: "2px solid #000",
        boxShadow: 24,
        p: 4,
    };

    useEffect(() => {
        const appsArray = user.apps
            ? typeof user.apps === 'string'
                ? user.apps.split(',').map(a => a.trim()).filter(Boolean)
                : Array.isArray(user.apps) ? user.apps : []
            : [];
        setSelectedApps(appsArray);
    }, [user]);

    const handleAppToggle = (appName) => {
        setAppToggle(prev => {
            const newToggle = { ...prev, [appName]: !prev[appName] };

            const isTurningOn = newToggle[appName];
            const isKot = appName.toUpperCase().includes("KOT") || appName.toUpperCase().includes("KEEP ON TOP");

            if (role !== "admin" && isKot && isTurningOn && availableKotRoles.length === 0) {
                fetchKotRoles(user.personid, [...tempSelectedApps, appName]);
            }

            const app = allApps.find(a => a.app_name === appName);
            const appIpAppIds = getAllIpAppIds(app);

            if (newToggle[appName]) {
                setTempSelectedApps(prev => [...new Set([...prev, appName])]);
                setTempSelectedIpApps(prev => addIpAppIds(prev, appIpAppIds));
            } else {
                setTempSelectedApps(prev => prev.filter(name => name !== appName));
                setTempSelectedIpApps(prev => removeIpAppIds(prev, appIpAppIds));
            }

            if (isKot) {
                setTempKotRole("");
            }

            return newToggle;
        });
    };

    const handleAppIntentsToggle = (appIpAppIds, isCurrentlyChecked) => {
        setTempSelectedIpApps(prev => toggleIpAppIds(prev, appIpAppIds, isCurrentlyChecked));
    };

    const handleCategoryToggle = (categoryIds, isCategoryChecked) => {
        if (isCategoryChecked) {
            setTempSelectedIpApps(prev =>
                prev.filter(id => !categoryIds.includes(id))
            );
        } else {
            setTempSelectedIpApps(prev => [
                ...new Set([...prev, ...categoryIds])
            ]);
        }
    };


    const handleIntentSelection = (ipAppId, appName) => {
        setTempSelectedIpApps(prev => {
            const isSelected = prev.includes(ipAppId);
            const newSelection = isSelected
                ? prev.filter(id => id !== ipAppId)
                : [...prev, ipAppId];

            // Auto-enable parent app if selecting an intent
            if (!isSelected && !tempSelectedApps.includes(appName)) {
                setTempSelectedApps(prev => [...prev, appName]);
                setAppToggle(prev => ({ ...prev, [appName]: true }));
            }

            return newSelection;
        });
    };

    const handleClickEdit = () => {
        const currentApps = user.apps
            ? typeof user.apps === 'string'
                ? user.apps.split(',').map(a => a.trim()).filter(Boolean)
                : Array.isArray(user.apps) ? user.apps : []
            : [];

        setTempSelectedApps(currentApps);

        const currentIpAppIds = Array.isArray(user.ip_apps) ? user.ip_apps : [];
        setTempSelectedIpApps(currentIpAppIds);

        const initialToggles = {};
        allApps.forEach(app => {
            const hasApp = currentApps.includes(app.app_name);
            const hasAnyIntent = currentIpAppIds.some(id => {
                if (!app.ipApps) return false;
                return Object.values(app.ipApps).some(items => {
                    if (Array.isArray(items)) {
                        return items.some(item => item.ip_app_id === id);
                    }
                    return items?.ip_app_id === id;
                });
            });

            if (hasApp || hasAnyIntent) {
                initialToggles[app.app_name] = true;
            }
        });
        setAppToggle(initialToggles);

        setOaShellAccess(!!user.oaRole);
        setOaRole(user.oaRole || "");
        setPgsAccess(!!user.pgsRole);
        setPgsRole(user.pgsRole || "");

        setOpenEdit(true);
    
    // Fetch KOT roles and User's KOT access asynchronously
    role !== 'admin' && fetchKotRoles(user.personid, currentApps);
};

const isKotApp = (appName = "") =>
  appName.toUpperCase().includes("KOT") ||
  appName.toUpperCase().includes("KEEP ON TOP");

const fetchKotRoles = async (personId, currentApps) => {
    const hasKotAccess = currentApps?.some(isKotApp);

    if (!hasKotAccess) return;

    try {
        setFetchingKotData(true);
        // Ensure we load roles definitions
        const rolesRes = await axios_tenant.get("/kot/admin/roles");
        if (rolesRes.data) {
            setAvailableKotRoles(rolesRes.data.filter(role => role.kotRoleId !== "ADMIN"));
        }

        // Fetch user's existing KOT access
        const accessRes = await axios_tenant.get(`/kot/admin/users/${personId}/access`);
        if (accessRes.data && accessRes.data.roles) {
            setTempKotRole(accessRes.data.roles);
        }
    } catch (e) {
        console.error("Failed to fetch KOT roles or access data", e);
    } finally {
        setFetchingKotData(false);
    }
};

const handleKotRoleSelection = (roleId) => {
    setTempKotRole(roleId);
};

    const handleDeleteSubmit = async personId => {
        setOpenDelete(false);
        if (user.status !== "pending") {
            try {
                const response = await axios_tenant.post(`/user/updateUserStatus/${personId}`, { userStatus: selectedValue });
                getUserData();
                toast.success(response.data.message);
            } catch (error) {
            }
        } else {
            toast.error("Account activation action pending by the user");
        }
    };

    const handleDeleteChange = event => {
        setSelectedValue(event.target.value);
    };

    const handleEditSubmit = async (personId) => {
        setLoading(true);
        try {
            const isKotAppSelected = tempSelectedApps.some(appName => 
                appName.toUpperCase().includes('KOT') || appName.toUpperCase().includes('KEEP ON TOP')
            );
            const { firstName, lastName, role, status } = inputData;
            // Save KOT Data First
            if (isKotAppSelected || user.apps?.includes('KOT') || user.apps?.includes('Keep On Top')) {
                await axios_tenant.post(`/kot/admin/users/${personId}/access`, {
                    hasKotAccess: isKotAppSelected,
                    roles: role !== "admin" ? tempKotRole : "ADMIN"
                });
            }
            const payload = {
                userData: {
                    firstName,
                    lastName,
                    role,
                    status,
                    apps: tempSelectedApps.join(','),
                    ip_apps: tempSelectedIpApps,
                    oaRole: oaShellAccess ? oaRole : "",
                    pgsRole: pgsAccess ? pgsRole : "",
                }
            };

            const response = await axios_tenant.post(`/user/editUserData/${personId}`, payload);

            toast.success(response.data.message || "User updated successfully");
            getUserData();
            setOpenEdit(false);
        } catch (error) {
            const errMsg = error.response?.data?.message || "Error updating user";
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleEditChange = e => {
        setInputData({ ...inputData, [e.target.name]: e.target.value });
    };

    const handleInvite = (e) => {
        e.preventDefault();
        setLoading(true);
        axios_tenant.post("/user/sendInvitation", { email, role })
            .then(() => {
                setLoading(false);
                toast.success(`Invite link sent to ${email}`);
            })
            .catch(err => {
                setLoading(false);
            });
    };

    return (
        <Card sx={{ mb: 1 }}>
            <Stack direction="row" alignItems={"center"} sx={{ p: theme => theme.spacing(1, 1) }}>
                <Item
                    sx={{
                        flex: { xs: 1, md: "0 1 20%", lg: "0 1 20%" },
                        alignSelf: "flex-center",
                        flexBasis: { md: "20%", lg: "20%" },
                        display: { xs: "none", md: "block" }
                    }}
                >
                    <Stack direction="row" alignItems={"center"}>
                        <Item>
                            <Badge
                                overlap="circular"
                                variant="dot"
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right"
                                }}
                                sx={{
                                    ".MuiBadge-badge": {
                                        border: "2px solid #FFF",
                                        height: "14px",
                                        width: "14px",
                                        borderRadius: "50%",
                                        bgcolor: user.isOnline ? "success.main" : "#757575"
                                    }
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 30,
                                        height: 30
                                    }}
                                    alt={`${user.first_name} 
                                    ${user.last_name}}`}
                                // src={user.profilePic}
                                />
                            </Badge>
                        </Item>
                        <Item>
                            <Typography variant="h6" mb={0.5} sx={{wordBreak: "break-word"}}>{user.first_name || user.last_name? `${user.first_name || ""} ${user.last_name || ""}`.trim(): ""}
                                
                            </Typography>
                        </Item>
                    </Stack>
                </Item>
                <Item
                    sx={{
                        flex: { xs: 1, md: "0 1 15%", lg: "0 1 15%" },
                        alignSelf: "flex-start",
                        flexBasis: { md: "15%", lg: "15%" },
                        display: { xs: "none", md: "block" },
                    }}
                >
                    <Typography variant="h6" mt={1} lineHeight={1.25}>
                        {user.role === "admin" ? "Admin" : "User"}
                    </Typography>
                </Item>

                <Item
                    sx={{
                        flex: { xs: 1, md: "0 1 25%", lg: "0 1 25%" },
                        alignSelf: "flex-start",
                        flexBasis: { md: "25%", lg: "25%" },
                        display: { xs: "none", md: "block" },
                    }}
                >
                    <Typography variant="h6" mt={1} lineHeight={1.25}>
                        {user.mail}
                    </Typography>
                </Item>
                <Item
                    sx={{
                        flex: { xs: 1, md: "0 1 15%", lg: "0 1 15%" },
                        alignSelf: "flex-start",
                        flexBasis: { md: "15%", lg: "15%" },
                        display: { xs: "none", md: "block" },
                    }}
                >
                    <Typography variant="h6" mt={1} lineHeight={1.25}>
                        {user.status}
                    </Typography>
                </Item>
                <Item
                    sx={{
                        flex: { xs: 1, md: "0 1 15%", lg: "0 1 15%" },
                        alignSelf: "flex-start",
                        flexBasis: { md: "15%", lg: "15%" },
                        display: { xs: "none", md: "block" },
                    }}
                >
                    <Typography variant="h6" mt={1} lineHeight={1.25}>
                        {allApps
                            .filter(
                                (app) =>
                                    app.TenantAppMapping.isactive && selectedApps.includes(app.app_name)
                            )
                            .map((app) => app.app_name)
                            .join(", ")}
                    </Typography>
                </Item>
                <Item
                    sx={{
                        flex: { xs: 1, md: "0 1 10%", lg: "0 1 10%" },
                        alignSelf: "flex-start",
                        flexBasis: { md: "10%", lg: "10%" },
                        display: { xs: "none", md: "block" },pl: 3,
                    }}
                >
                    <Typography variant="h6" mt={1} lineHeight={1.25}>
                        <Edit onClick={handleClickEdit} style={{ cursor: "pointer" }} />
                        {/* <Delete onClick={handleClickDelete} style={{ cursor: "pointer" }} /> */}

                        {
                            status === "pending" ?
                                (loading ? <CircularProgress sx={{ color: "black" }} size="5%" /> :
                                    <SyncIcon onClick={handleInvite} style={{ cursor: "pointer" }} />) : ""
                        }


                        {/* <CircularProgress sx={{ color: 'black'}} size="15%"/> */}
                    </Typography>
                </Item>
                <Modal
                    open={openEdit}
                    onClose={() => setOpenEdit(false)}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Div sx={style}>
                        <Typography id="modal-modal-title" variant="h4" component="h2">
                            Edit User Data
                        </Typography>
                        <br />
                        <FormControl fullWidth>
                            {status !== "pending" && (
                                <TextField
                                    sx={{ mb: 2 }}
                                    name="firstName"
                                    label="First Name"
                                    value={firstName}
                                    onChange={handleEditChange}
                                />
                            )}
                            {status !== "pending" && (
                                <TextField
                                    sx={{ mb: 2 }}
                                    name="lastName"
                                    label="Last Name"
                                    value={lastName}
                                    onChange={handleEditChange}
                                />
                            )}
                            <TextField
                                sx={{ mb: 2 }}
                                name="email"
                                label="Email"
                                value={email}
                                disabled
                            />

                            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                                <InputLabel id="role-label">Role</InputLabel>
                                <Select labelId="role-label" id="role" label="Role" name="role" value={role} onChange={handleEditChange}>
                                    <MenuItem value="admin">Admin</MenuItem>
                                    <MenuItem value="user">User</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                                <InputLabel id="status-label">Status</InputLabel>
                                <Select labelId="status-label" id="status" label="Status" name="status" value={status} onChange={handleEditChange}>
                                    {status !== "pending" && <MenuItem value="active">Active</MenuItem>}
                                    <MenuItem value="deactivated">Deactivate</MenuItem>
                                    {status === "pending" && <MenuItem value="pending">Pending</MenuItem>}
                                </Select>
                            </FormControl>
                        </FormControl>

                        <Typography variant="h6" mt={2}>
                            Authorized Apps
                        </Typography>
                        {/* Wrap the FormGroup in a Box to add scroll */}
                        <Box
                            sx={{
                                maxHeight: 300,
                                overflowY: "auto",
                                border: "1px solid #eee",
                                p: 1,
                                borderRadius: 1
                            }}
                        >
                            {allApps.map(app => {
                                if (!app.TenantAppMapping.isactive) return null;

                                const { appIpAppIds, areAllAppIntentsChecked, areSomeAppIntentsChecked } =
                                    getAppIntentSelectionState(app, tempSelectedIpApps);

                                return (
                                    <Box key={app.app_id} sx={{ mb: 2 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {appToggle[app.app_name] && appIpAppIds.length > 0 && (
                                                    <Checkbox
                                                        checked={areAllAppIntentsChecked}
                                                        indeterminate={areSomeAppIntentsChecked}
                                                        onChange={() => handleAppIntentsToggle(appIpAppIds, areAllAppIntentsChecked)}
                                                        sx={{ mr: 0.5 }}
                                                    />
                                                )}
                                                <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{app.app_name}</Typography>
                                            </Box>
                                            <Switch
                                                checked={appToggle[app.app_name] || false}
                                                onChange={() => handleAppToggle(app.app_name)}
                                            />
                                        </Box>

                                         {/* OA Shell & Post Grant Support Workflows Access Checkbox + Role Pickers */}
                                         {app.app_name === "COLLABORATE" && appToggle["COLLABORATE"] && inputData.role !== "admin" && isOaWorkflowTenant && (
                                             <Box sx={{ ml: 2, mb: 2, p: 1.5, border: "1px dashed #ccc", borderRadius: 1, backgroundColor: "#fafafa" }}>
                                                 <Box sx={{ mb: 2 }}>
                                                     <FormControlLabel
                                                         control={<Checkbox checked={oaShellAccess} onChange={(e) => {
                                                             setOaShellAccess(e.target.checked);
                                                             if (e.target.checked && !oaRole) setOaRole("Application Manager");
                                                             if (!e.target.checked) setOaRole("");
                                                         }} />}
                                                         label={<Typography variant="body2" fontWeight="600">OA Shell Workflow</Typography>}
                                                     />
                                                     {oaShellAccess && (
                                                         <Box sx={{ ml: 3, mt: 0.5 }}>
                                                             <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Select Role:</Typography>
                                                             <RadioGroup row value={oaRole} onChange={(e) => setOaRole(e.target.value)}>
                                                                 <FormControlLabel
                                                                     value="Application Manager"
                                                                     control={<Radio size="small" />}
                                                                     label={<Typography variant="body2">Application Manager</Typography>}
                                                                 />
                                                                 <FormControlLabel
                                                                     value="Processor"
                                                                     control={<Radio size="small" />}
                                                                     label={<Typography variant="body2">Processor</Typography>}
                                                                 />
                                                                 <FormControlLabel
                                                                     value="QA"
                                                                     control={<Radio size="small" />}
                                                                     label={<Typography variant="body2">QA</Typography>}
                                                                 />
                                                             </RadioGroup>
                                                         </Box>
                                                     )}
                                                 </Box>
                                                 <Box>
                                                     <FormControlLabel
                                                         control={<Checkbox checked={pgsAccess} onChange={(e) => {
                                                             setPgsAccess(e.target.checked);
                                                             if (e.target.checked && !pgsRole) setPgsRole("Application Manager");
                                                             if (!e.target.checked) setPgsRole("");
                                                         }} />}
                                                         label={<Typography variant="body2" fontWeight="600">Post Grant Support Workflow</Typography>}
                                                     />
                                                     {pgsAccess && (
                                                         <Box sx={{ ml: 3, mt: 0.5 }}>
                                                             <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Select Role:</Typography>
                                                             <RadioGroup row value={pgsRole} onChange={(e) => setPgsRole(e.target.value)}>
                                                                 <FormControlLabel
                                                                     value="Application Manager"
                                                                     control={<Radio size="small" />}
                                                                     label={<Typography variant="body2">Application Manager</Typography>}
                                                                 />
                                                                 <FormControlLabel
                                                                     value="Processor"
                                                                     control={<Radio size="small" />}
                                                                     label={<Typography variant="body2">Processor</Typography>}
                                                                 />
                                                                 <FormControlLabel
                                                                     value="QA"
                                                                     control={<Radio size="small" />}
                                                                     label={<Typography variant="body2">QA</Typography>}
                                                                 />
                                                             </RadioGroup>
                                                         </Box>
                                                     )}
                                                 </Box>
                                             </Box>
                                         )}

                                        {(appToggle[app.app_name] && app.ipApps) && (
                                            <Box sx={{ ml: 2 }}>
                                                {Object.entries(app.ipApps).map(([category, items], index, array) => {
                                                    const isArray = Array.isArray(items);
                                                    const itemArray = isArray ? sortIntentsByCategory(category, items) : [items];
                                                    const categoryIds = itemArray.map(item => item.ip_app_id);

                                                    const checkedCount = categoryIds.filter(id =>
                                                        tempSelectedIpApps.includes(id)
                                                    ).length;

                                                    const isCategoryChecked =
                                                        checkedCount === categoryIds.length && categoryIds.length > 0;

                                                    const isIndeterminate =
                                                        checkedCount > 0 && checkedCount < categoryIds.length;

                                                    return (
                                                        <Box key={category} sx={{ mt: 1 }}>

                                                            {/* CATEGORY CHECKBOX */}
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={isCategoryChecked}
                                                                        indeterminate={isIndeterminate}
                                                                        onChange={() =>
                                                                            handleCategoryToggle(categoryIds, isCategoryChecked)
                                                                        }
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                                                        {category}
                                                                    </Typography>
                                                                }
                                                            />

                                                            {/* ITEMS LIST */}
                                                            {
                                                                isArray && (
                                                                    <Box sx={{ ml: 3 }}>
                                                                        {itemArray.map(item => (
                                                                            <FormControlLabel
                                                                                key={item.ip_app_id}
                                                                                control={
                                                                                    <Checkbox
                                                                                        checked={tempSelectedIpApps.includes(item.ip_app_id)}
                                                                                        onChange={() => handleIntentSelection(item.ip_app_id, app.app_name)}
                                                                                    />
                                                                                }
                                                                                label={item.subtask_name}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                )
                                                            }
                                                            {index < array.length - 1 && (
                                                                <Divider sx={{ my: 2, mx: -4, backgroundColor: "#475259", border: '1px solid' }} />
                                                            )}

                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                )}

                                {/* KOT ROLES UI */}
                                { (role !== "admin" && appToggle[app.app_name] && 
                                  (app.app_name.toUpperCase().includes('KOT') || app.app_name.toUpperCase().includes('KEEP ON TOP'))) && (
                                    <Box sx={{ ml: 2, mt: 1 }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>
                                            KOT Roles
                                        </Typography>
                                        
                                        {fetchingKotData ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <Box sx={{ ml: 1 }} display={"flex"}>
                                                <RadioGroup
                                                    value={tempKotRole ||""}
                                                    onChange={(e) => handleKotRoleSelection(e.target.value)}
                                                    row
                                                >
                                                    {availableKotRoles.map((role) => (
                                                        <FormControlLabel
                                                            key={role.kotRoleId}
                                                            value={role.kotRoleId}
                                                            control={<Radio />}
                                                            label={role.name}
                                                        />
                                                    ))}
                                                </RadioGroup>

                                                {availableKotRoles.length === 0 && (
                                                    <Typography variant="body2" color="textSecondary">
                                                        No roles available
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                        <Divider sx={{ my: 2, mx: -2, backgroundColor: "#475259", border:'1px solid' }} />
                                    </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box textAlign="right" sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center", justifyContent: "flex-end" }}>
                            { loading ? 
                                <CircularProgress size={20} color="primary" /> : 
                                <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                onClick={() => handleEditSubmit(user.personid)}
                                disabled={loading}>
                                    Submit
                                </Button>
                            }
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                onClick={() => setOpenEdit(false)}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Div>
                </Modal>

                <Modal open={openDelete} onClose={() => setOpenDelete(false)} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                    <Div sx={style}>
                        <Typography id="modal-modal-title" variant="h4" component="h2">
                            User Status
                        </Typography>
                        <br></br>
                        <Typography id="modal-modal-title" variant="h4" component="h2">
                            <FormControl fullWidth>
                                <RadioGroup value={selectedValue} onChange={handleDeleteChange}>
                                    {/* <FormControlLabel value="active" control={<Radio />} label="Active" /> */}
                                    <FormControlLabel value="deactivated" control={<Radio />} label="Deactivated" />
                                </RadioGroup>
                            </FormControl>
                        </Typography>

                        <Box textAlign="right" sx={{ mt: 2 }}>
                            <Button type="submit" variant="contained" color="primary" onClick={() => handleDeleteSubmit(user.personid)}>
                                Submit
                            </Button>{" "}
                            <Button type="submit" variant="contained" color="primary" onClick={() => setOpenDelete(false)}>
                                Cancel
                            </Button>
                        </Box>
                    </Div>
                </Modal>
            </Stack>
        </Card>
    );
};

export default UserCard;